/**
 * MCP Tools for Customer Intelligence
 * 
 * These tools allow AI Agents to access and manage Customer Intelligence data.
 * Customer Intelligence is the central knowledge base for prospects/customers,
 * containing brand data, voice analysis, keywords, and visual identity.
 * 
 * Used by: ResearchAgent, AdsStrategistAgent, CreativeAgent, OrchestratorAgent
 */

import { MCPTool } from '../types';
import { customerIntelligenceService } from '../../services/customerIntelligenceService';
import { salesDossierService } from '../../services/salesDossierService';
import { getMCPServer } from '../server/MCPServer';

// ============================================================================
// GET CUSTOMER INTELLIGENCE
// ============================================================================

export const getCustomerIntelligenceTool: MCPTool = {
  name: 'get_customer_intelligence',
  description: `Retrieves Customer Intelligence data for a domain or URL.
Returns comprehensive brand data including:
- Business info (name, industry, USP, offerings)
- Voice analysis (tone, address form, key messages)
- Keywords (core, long-tail, negative)
- Visual identity (colors, logo, images)

Use this to understand a prospect/customer before creating campaigns or content.`,
  
  inputSchema: {
    type: 'object',
    properties: {
      domain: {
        type: 'string',
        description: 'Domain or URL to look up (e.g., "example.com" or "https://example.com")'
      },
      ciId: {
        type: 'string',
        description: 'Alternatively, provide the Customer Intelligence ID directly'
      }
    },
    required: []
  },

  async execute(params, context) {
    try {
      if (!params.domain && !params.ciId) {
        return {
          success: false,
          error: 'Either domain or ciId must be provided'
        };
      }

      let ci;
      
      if (params.ciId) {
        ci = await customerIntelligenceService.getById(context.orgId, params.ciId);
      } else {
        ci = await customerIntelligenceService.findByDomain(context.orgId, params.domain);
      }

      if (!ci) {
        return {
          success: false,
          found: false,
          message: `No Customer Intelligence found for ${params.domain || params.ciId}. Use 'create_customer_intelligence' to analyze this prospect.`
        };
      }

      // Return a structured summary for the AI
      return {
        success: true,
        found: true,
        customerIntelligence: {
          id: ci.id,
          url: ci.url,
          domain: ci.domain,
          businessName: ci.businessName,
          industry: ci.brand.industry,
          businessModel: ci.brand.businessModel,
          
          // Brand essentials
          brand: {
            usp: ci.brand.usp,
            slogan: ci.brand.slogan,
            offerings: ci.brand.offerings,
            targetAudience: ci.brand.targetAudience,
            keyBenefits: ci.brand.keyBenefits,
            primaryCTA: ci.brand.primaryCTA
          },
          
          // Voice for consistent messaging
          voice: {
            addressForm: ci.voice?.addressForm || 'Sie',
            formality: ci.voice?.formality || 'formal',
            toneAttributes: ci.voice?.toneAttributes || [],
            keyMessages: ci.voice?.keyMessages || [],
            doStatements: ci.voice?.doStatements || [],
            dontStatements: ci.voice?.dontStatements || []
          },
          
          // Keywords for campaigns
          keywords: {
            core: ci.keywords.core,
            longTail: ci.keywords.longTail,
            negative: ci.keywords.negative,
            industrySpecific: ci.keywords.industrySpecific || []
          },
          
          // Visual identity
          visual: {
            logoUrl: ci.visual?.logoUrl,
            brandColors: ci.visual?.brandColors || [],
            primaryColor: ci.visual?.primaryColor,
            visualStyle: ci.visual?.visualStyle
          },
          
          // Metadata
          language: ci.language,
          location: ci.location,
          lifecycleStatus: ci.lifecycle?.status,
          lastUpdated: ci.updatedAt
        }
      };
    } catch (error: any) {
      return {
        success: false,
        error: `Failed to get Customer Intelligence: ${error.message}`
      };
    }
  }
};

// ============================================================================
// CREATE/ANALYZE CUSTOMER INTELLIGENCE
// ============================================================================

export const createCustomerIntelligenceTool: MCPTool = {
  name: 'create_customer_intelligence',
  description: `Analyzes a website and creates a new Customer Intelligence record.
This performs deep analysis including:
- Website scraping and content extraction
- AI-powered brand/voice analysis
- Keyword extraction
- Visual identity detection
- Industry classification

Use this when you need to understand a new prospect or update stale data.`,
  
  inputSchema: {
    type: 'object',
    properties: {
      url: {
        type: 'string',
        description: 'Website URL to analyze (e.g., "https://example.com")'
      },
      includePageSpeed: {
        type: 'boolean',
        description: 'Include PageSpeed insights (default: true)'
      },
      includeCompetitors: {
        type: 'boolean',
        description: 'Include competitor analysis (default: false)'
      }
    },
    required: ['url']
  },

  async execute(params, context) {
    try {
      // Check if CI already exists
      const existing = await customerIntelligenceService.findByDomain(context.orgId, params.url);
      
      if (existing) {
        return {
          success: true,
          alreadyExists: true,
          message: `Customer Intelligence already exists for this domain. Use 'get_customer_intelligence' to retrieve it or 'update_customer_intelligence' to refresh.`,
          ciId: existing.id,
          businessName: existing.businessName
        };
      }

      // Create new analysis
      const ci = await customerIntelligenceService.analyzeProspect(
        context.orgId,
        params.url,
        context.userId,
        {
          includePageSpeed: params.includePageSpeed !== false,
          includeCompetitors: params.includeCompetitors || false
        }
      );

      return {
        success: true,
        created: true,
        customerIntelligence: {
          id: ci.id,
          domain: ci.domain,
          businessName: ci.businessName,
          industry: ci.brand.industry,
          usp: ci.brand.usp,
          keywordCount: ci.keywords.core.length + ci.keywords.longTail.length,
          voiceAnalyzed: !!ci.voice,
          language: ci.language
        },
        message: `Customer Intelligence created for "${ci.businessName}"`
      };
    } catch (error: any) {
      return {
        success: false,
        error: `Failed to create Customer Intelligence: ${error.message}`
      };
    }
  }
};

// ============================================================================
// UPDATE/REFRESH CUSTOMER INTELLIGENCE
// ============================================================================

export const updateCustomerIntelligenceTool: MCPTool = {
  name: 'update_customer_intelligence',
  description: `Updates or refreshes an existing Customer Intelligence record.
Re-analyzes the website to update brand data, keywords, and voice analysis.
Use this when data might be stale or after significant website changes.`,
  
  inputSchema: {
    type: 'object',
    properties: {
      ciId: {
        type: 'string',
        description: 'Customer Intelligence ID to update'
      },
      domain: {
        type: 'string',
        description: 'Alternatively, provide domain to look up and update'
      }
    },
    required: []
  },

  async execute(params, context) {
    try {
      if (!params.ciId && !params.domain) {
        return {
          success: false,
          error: 'Either ciId or domain must be provided'
        };
      }

      // Find existing CI
      let existing;
      if (params.ciId) {
        existing = await customerIntelligenceService.getById(context.orgId, params.ciId);
      } else {
        existing = await customerIntelligenceService.findByDomain(context.orgId, params.domain);
      }

      if (!existing) {
        return {
          success: false,
          error: `Customer Intelligence not found. Use 'create_customer_intelligence' first.`
        };
      }

      // Re-analyze with existing ID
      const updated = await customerIntelligenceService.analyzeProspect(
        context.orgId,
        existing.url,
        context.userId,
        { existingId: existing.id }
      );

      return {
        success: true,
        updated: true,
        customerIntelligence: {
          id: updated.id,
          domain: updated.domain,
          businessName: updated.businessName,
          industry: updated.brand.industry,
          lastUpdated: updated.updatedAt
        },
        message: `Customer Intelligence updated for "${updated.businessName}"`
      };
    } catch (error: any) {
      return {
        success: false,
        error: `Failed to update Customer Intelligence: ${error.message}`
      };
    }
  }
};

// ============================================================================
// LIST CUSTOMER INTELLIGENCE
// ============================================================================

export const listCustomerIntelligenceTool: MCPTool = {
  name: 'list_customer_intelligence',
  description: `Lists all Customer Intelligence records for the organization.
Returns a summary of each record including business name, industry, and status.
Use this to see what prospects/customers have been analyzed.`,
  
  inputSchema: {
    type: 'object',
    properties: {
      limit: {
        type: 'number',
        description: 'Maximum number of records to return (default: 20)'
      },
      industry: {
        type: 'string',
        description: 'Filter by industry'
      },
      status: {
        type: 'string',
        enum: ['prospect', 'lead', 'opportunity', 'customer', 'churned'],
        description: 'Filter by lifecycle status'
      }
    },
    required: []
  },

  async execute(params, context) {
    try {
      const records = await customerIntelligenceService.list(context.orgId, {
        limit: params.limit || 20
      });

      // Filter by industry if specified
      let filtered = records;
      if (params.industry) {
        filtered = records.filter(r => 
          r.industry.toLowerCase().includes(params.industry.toLowerCase())
        );
      }
      if (params.status) {
        filtered = filtered.filter(r => r.lifecycleStatus === params.status);
      }

      return {
        success: true,
        count: filtered.length,
        records: filtered.map(r => ({
          id: r.id,
          businessName: r.businessName,
          domain: r.domain,
          industry: r.industry,
          status: r.lifecycleStatus,
          hasGoogleAds: r.hasGoogleAds,
          createdAt: r.createdAt,
          updatedAt: r.updatedAt
        }))
      };
    } catch (error: any) {
      return {
        success: false,
        error: `Failed to list Customer Intelligence: ${error.message}`
      };
    }
  }
};

// ============================================================================
// GET CAMPAIGN CONTEXT (Convenience wrapper)
// ============================================================================

export const getCampaignContextFromCITool: MCPTool = {
  name: 'get_campaign_context_from_ci',
  description: `Extracts campaign-ready context from Customer Intelligence.
Returns data optimized for Google Ads campaign creation including:
- Brand messaging (USP, benefits, CTA)
- Voice guidelines (address form, tone)
- Keywords (core, long-tail, negative)
- Target audience info

Use this before creating campaigns to ensure brand consistency.`,
  
  inputSchema: {
    type: 'object',
    properties: {
      domain: {
        type: 'string',
        description: 'Domain to get campaign context for'
      },
      ciId: {
        type: 'string',
        description: 'Or provide CI ID directly'
      }
    },
    required: []
  },

  async execute(params, context) {
    try {
      if (!params.domain && !params.ciId) {
        return {
          success: false,
          error: 'Either domain or ciId must be provided'
        };
      }

      let ci;
      if (params.ciId) {
        ci = await customerIntelligenceService.getById(context.orgId, params.ciId);
      } else {
        ci = await customerIntelligenceService.findByDomain(context.orgId, params.domain);
      }

      if (!ci) {
        return {
          success: false,
          found: false,
          message: 'No Customer Intelligence found. Create one first.'
        };
      }

      // Extract campaign context
      const campaignContext = customerIntelligenceService.getCampaignContext(ci);
      const contentContext = customerIntelligenceService.getContentContext(ci);

      return {
        success: true,
        campaignContext: {
          url: campaignContext.url,
          businessName: campaignContext.businessName,
          industry: campaignContext.industry,
          targetAudience: campaignContext.targetAudience,
          usp: campaignContext.usp,
          keyBenefits: campaignContext.keyBenefits,
          offerings: campaignContext.offerings,
          keywords: campaignContext.keywords,
          language: campaignContext.language,
          location: campaignContext.location,
          voice: campaignContext.voice
        },
        contentContext: {
          voice: contentContext.voice,
          primaryCTA: contentContext.primaryCTA
        },
        readyForCampaign: true
      };
    } catch (error: any) {
      return {
        success: false,
        error: `Failed to get campaign context: ${error.message}`
      };
    }
  }
};

// ============================================================================
// GENERATE SALES DOSSIER
// ============================================================================

export const generateSalesDossierTool: MCPTool = {
  name: 'generate_sales_dossier',
  description: `Generates a comprehensive sales dossier from Customer Intelligence data.
A dossier includes:
- Executive summary for sales pitch
- Identified pain points (technical, marketing, brand)
- Growth opportunities
- Competitive analysis
- Tailored recommendations
- Example ad copy and image prompts

Use this to prepare for customer acquisition meetings.`,
  
  inputSchema: {
    type: 'object',
    properties: {
      domain: {
        type: 'string',
        description: 'Domain to generate dossier for (e.g., "example.com")'
      },
      ciId: {
        type: 'string',
        description: 'Or provide Customer Intelligence ID directly'
      },
      language: {
        type: 'string',
        enum: ['de', 'en'],
        description: 'Language for the dossier (default: de)'
      },
      includeExamples: {
        type: 'boolean',
        description: 'Include example ad copy and image prompts (default: true)'
      },
      includePresentation: {
        type: 'boolean',
        description: 'Generate a Gamma presentation (default: false)'
      }
    },
    required: []
  },

  async execute(params, context) {
    try {
      if (!params.domain && !params.ciId) {
        return {
          success: false,
          error: 'Either domain or ciId must be provided'
        };
      }

      let ciId = params.ciId;
      
      // Find CI by domain if not provided
      if (!ciId && params.domain) {
        const ci = await customerIntelligenceService.findByDomain(context.orgId, params.domain);
        if (!ci) {
          return {
            success: false,
            error: `No Customer Intelligence found for ${params.domain}. Create one first using 'create_customer_intelligence'.`
          };
        }
        ciId = ci.id;
      }

      const dossier = await salesDossierService.generateDossier(
        context.orgId,
        ciId!,
        {
          language: params.language || 'de',
          includeExamples: params.includeExamples !== false,
          includePresentation: params.includePresentation || false
        }
      );

      return {
        success: true,
        dossier: {
          id: dossier.id,
          businessName: dossier.businessName,
          industry: dossier.industry,
          executiveSummary: dossier.executiveSummary,
          
          painPoints: dossier.painPoints.map(p => ({
            category: p.category,
            severity: p.severity,
            title: p.title,
            description: p.description,
            impact: p.impact
          })),
          
          opportunities: dossier.opportunities.map(o => ({
            category: o.category,
            priority: o.priority,
            title: o.title,
            expectedImpact: o.expectedImpact
          })),
          
          recommendations: dossier.recommendations.map(r => ({
            service: r.service,
            priority: r.priority,
            estimatedImpact: r.estimatedImpact
          })),
          
          competitiveInsights: dossier.competitiveInsights,
          brandAnalysis: dossier.brandAnalysis,
          exampleAssets: dossier.exampleAssets,
          presentationUrl: dossier.presentationUrl
        },
        message: `Sales dossier generated for "${dossier.businessName}" with ${dossier.painPoints.length} pain points and ${dossier.recommendations.length} recommendations`
      };
    } catch (error: any) {
      return {
        success: false,
        error: `Failed to generate sales dossier: ${error.message}`
      };
    }
  }
};

// ============================================================================
// REGISTER ALL CUSTOMER INTELLIGENCE TOOLS
// ============================================================================

export function registerCustomerIntelligenceTools(): void {
  const server = getMCPServer();
  
  server.registerTool(getCustomerIntelligenceTool, {
    category: 'customer-intelligence',
    requiresAuth: true,
    dangerLevel: 'safe'
  });
  
  server.registerTool(createCustomerIntelligenceTool, {
    category: 'customer-intelligence',
    requiresAuth: true,
    dangerLevel: 'moderate'
  });
  
  server.registerTool(updateCustomerIntelligenceTool, {
    category: 'customer-intelligence',
    requiresAuth: true,
    dangerLevel: 'moderate'
  });
  
  server.registerTool(listCustomerIntelligenceTool, {
    category: 'customer-intelligence',
    requiresAuth: true,
    dangerLevel: 'safe'
  });
  
  server.registerTool(getCampaignContextFromCITool, {
    category: 'customer-intelligence',
    requiresAuth: true,
    dangerLevel: 'safe'
  });
  
  server.registerTool(generateSalesDossierTool, {
    category: 'customer-intelligence',
    requiresAuth: true,
    dangerLevel: 'safe'
  });
  
  console.log('✅ Registered 6 Customer Intelligence MCP tools');
}

